import api from '../services/api';

/* ==========================================================================
   Statistiky → Nastavení sazeb :: MOST MEZI API A OBRAZOVKOU

   Nahrazuje statisticsSettingsMockData.js. Obrazovka pracuje s tvary, které
   vznikly v mockupu (tituly klíčované kódem `T-NK`, pásma jako {kod, pct}),
   kdežto API vrací databázový tvar. Překlad je schválně tady, aby JSX
   obrazovky zůstalo, jak bylo — mění se zdroj dat, ne podoba.

   Zpět do API se posílají jen ta pole, která se opravdu měnila.
   ========================================================================== */

/* ------------------------------------------------------- API → obrazovka */

/** Načte kompletní nastavení a přeloží ho do tvarů, které obrazovka čeká. */
export async function nactiNastaveni() {
  const { data } = await api.getStatisticsSettings();

  return {
    /* metadata verze sazeb */
    rateSetId: data.rateSet.id,
    platnostOd: data.rateSet.validFrom,
    verze: data.rateSet.versions,
    nezarazenaMedia: data.unassignedMedia,

    /* pásma provize z vydání */
    pasma: data.rateBands.map((p) => ({ kod: p.code, pct: p.percentage, nazev: p.name })),

    /* skalární sazby */
    zaklady: {
      partner: data.settings.partner_base?.value ?? 0.12,
      special: data.settings.special_base?.value ?? 0.10,
      penalizace: data.settings.discount_penalty?.value ?? 0.005,
      min: data.settings.min_commission?.value ?? 0,
    },

    /* bonusová pásma */
    bonusPasma: data.bonusBands.map((b) => ({ od: b.threshold, pct: b.percentage })),

    /* korekce splatnosti — poslední pásmo má v DB hranici 999999 = „a více" */
    korekce: data.dueCorrections.map((k) => ({
      kod: k.coefficient >= 1 ? 'ok' : (k.coefficient > 0 ? 'half' : 'zero'),
      do: k.daysTo >= 999999 ? null : k.daysTo,
      koef: k.coefficient,
      nazev: k.name,
      popis: k.note,
      ikona: k.coefficient >= 1 ? 'bi-check-circle-fill'
        : (k.coefficient > 0 ? 'bi-exclamation-triangle-fill' : 'bi-x-circle-fill'),
    })),

    /* podmínky nároku na bonus */
    podminky: data.conditions.map((p) => ({
      id: p.code, nazev: p.name, popis: p.description, aktivni: p.isActive,
    })),

    /* velikosti mutací → { mala: {p11..p20, naklad}, … } */
    mutacePlany: Object.fromEntries(data.mutationSizes.map((v) => [v.code, {
      p11: v.p11, p14: v.p14, p17: v.p17, p20: v.p20, naklad: v.circulationNote,
    }])),
    velikostiMutaci: data.mutationSizes.map((v) => ({ id: v.code, label: v.name })),

    /* tituly — obrazovka je adresuje kódem, DB číslem, držíme obojí */
    tituly: data.titles.map((t) => ({
      id: t.code,
      dbId: t.id,
      nazev: t.name,
      typ: t.type,
      kraj: t.region,
      lokalita: t.locality,
      periodicita: t.periodicity,
      stran: t.pages,
      pocetMedii: t.mediaCount,
      plany: {
        p11: t.plan?.p11 ?? 0, p14: t.plan?.p14 ?? 0,
        p17: t.plan?.p17 ?? 0, p20: t.plan?.p20 ?? 0,
      },
      planPlatiOd: t.plan?.validFrom ?? null,
      slevy: {
        oz: t.discount?.oz ?? null,
        manazerTitulu: t.discount?.managerTitle ?? null,
        manazerRegionu: t.discount?.managerRegion ?? null,
        maxBezSouhlasu: t.discount?.maxWithoutApproval ?? null,
      },
      slevaPlatiOd: t.discount?.validFrom ?? null,
      vyznamne: t.isSignificant,
      vyznamneNa: t.significantAt,
      bezBonusu: t.noBonus,
      zakladProvize: t.baseCommission,
      mesicniAgregace: t.monthlyAggregation,
      planNeurcen: t.planUndefined,
      planZMutaci: t.planFromMutations,
      poznamka: t.note,
      provizePozn: t.commissionNote,
      slevaPozn: t.discountNote,
    })),

    /* obchodníci a manažeři */
    obchodnici: data.salespeople.map((o) => ({
      id: o.id, jmeno: o.name, manazer: o.managerName, manazerId: o.managerId,
      pevnyBonus: o.fixedBonusPct, bezBrany: o.bypassGate, aktivni: o.isActive,
      /* Topmanagement nevstupuje do základu superprovize (migrace 051). */
      topmanagement: o.isTopManagement,
    })),
    manazeri: data.managers.map((m) => ({
      id: m.id, jmeno: m.name, role: m.role, obchodnikId: m.salespersonId,
      superprovize: m.superCommissionPct, gesce: m.scope,
      /* 'team' = vlastní tým (i vnořený), 'company' = všichni mimo
         topmanagement (obchodní ředitel). */
      rozsah: m.superScope, typMedia: m.superMediaType, aktivni: m.isActive,
    })),
    mentoring: (data.mentorships || []).map((m) => ({
      id: m.id,
      mentorovanyId: m.menteeId,
      mentorovany: m.menteeName,
      mentorId: m.mentorId,
      mentor: m.mentorName,
      pct: m.pct,
      od: m.validFrom,
      do: m.validTo,
      poznamka: m.note,
      /* 'planned' | 'running' | 'ended' — počítá server podle dnešního data. */
      stav: m.state,
    })),
    typyMedii: (data.mediaTypes || []).map((t) => ({
      id: t.id, nazev: t.label, pocetMedii: t.mediumCount,
    })),
  };
}

/** Média sdružená pod titul — pro záložku mutací a klasifikace médií. */
export async function nactiMedia(params = {}) {
  const { data } = await api.getStatisticsMedia(params);
  return data.map((m) => ({
    id: m.id,
    nazev: m.name,
    typMedia: m.mediaType,
    titulId: m.titleId,
    titulKod: m.titleCode,
    titulNazev: m.titleName,
    titulTyp: m.titleType,
    velikost: m.sizeCode,
    stran: m.pages,
    pocetProjektu: m.projectCount,
  }));
}

/* ------------------------------------------------------- obrazovka → API */

/**
 * Uloží sazby jako verzi platnou od data.
 *
 * Backend novou verzi předvyplní klonem té současné, takže se posílají jen
 * měněné číselníky. Stará verze zůstane — uzavřená období se počítají
 * sazbami, které pro ně platily.
 */
export function ulozSazby(platnostOd, cast) {
  const payload = { validFrom: platnostOd };

  if (cast.pasma) {
    payload.rateBands = cast.pasma.map((p, i) => ({
      code: p.kod, percentage: p.pct, name: p.nazev, sortOrder: i,
    }));
  }
  if (cast.zaklady) {
    payload.settings = {
      partner_base: cast.zaklady.partner,
      special_base: cast.zaklady.special,
      discount_penalty: cast.zaklady.penalizace,
      min_commission: cast.zaklady.min,
    };
  }
  if (cast.bonusPasma) {
    payload.bonusBands = cast.bonusPasma
      .slice()
      .sort((a, b) => a.od - b.od)
      .map((b, i) => ({ threshold: b.od, percentage: b.pct, sortOrder: i }));
  }
  if (cast.korekce) {
    payload.dueCorrections = cast.korekce.map((k, i) => ({
      daysTo: k.do === null || k.do === '' ? 999999 : Number(k.do),
      coefficient: k.koef, name: k.nazev, note: k.popis, sortOrder: i,
    }));
  }
  if (cast.podminky) {
    payload.conditions = cast.podminky.map((p, i) => ({
      code: p.id, name: p.nazev, description: p.popis, isActive: p.aktivni ? 1 : 0, sortOrder: i,
    }));
  }
  if (cast.mutacePlany) {
    payload.mutationSizes = Object.entries(cast.mutacePlany).map(([kod, v], i) => ({
      code: kod, name: v.nazev || kod, circulationNote: v.naklad,
      p11: v.p11, p14: v.p14, p17: v.p17, p20: v.p20, sortOrder: i,
    }));
  }

  return api.saveStatisticsRates(payload);
}

/** Uloží titul — klasifikaci, popisné údaje a volitelně plán či slevy. */
export function ulozTitul(titul, { plan = false, slevy = false } = {}) {
  const payload = {
    type: titul.typ,
    region: titul.kraj,
    locality: titul.lokalita,
    periodicity: titul.periodicita,
    pages: titul.stran,
    isSignificant: titul.vyznamne,
    significantAt: titul.vyznamneNa,
    noBonus: titul.bezBonusu,
    monthlyAggregation: titul.mesicniAgregace,
    planUndefined: titul.planNeurcen,
    planFromMutations: titul.planZMutaci,
    baseCommission: titul.zakladProvize,
    note: titul.poznamka,
    commissionNote: titul.provizePozn,
    discountNote: titul.slevaPozn,
  };

  if (plan) {
    payload.plan = {
      validFrom: titul.planPlatiOd || new Date().toISOString().slice(0, 8) + '01',
      p11: titul.plany.p11, p14: titul.plany.p14,
      p17: titul.plany.p17, p20: titul.plany.p20,
    };
  }
  if (slevy) {
    payload.discount = {
      validFrom: titul.slevaPlatiOd || new Date().toISOString().slice(0, 10),
      oz: titul.slevy.oz,
      managerTitle: titul.slevy.manazerTitulu,
      managerRegion: titul.slevy.manazerRegionu,
      maxWithoutApproval: titul.slevy.maxBezSouhlasu,
    };
  }

  return api.saveStatisticsTitle(titul.dbId, payload);
}

/** Individuální úpravy obchodníka (pevný bonus, nárok bez brány, nadřízený). */
export function ulozObchodnika(id, zmena) {
  const payload = {};
  if ('pevnyBonus' in zmena) payload.fixedBonusPct = zmena.pevnyBonus;
  if ('bezBrany' in zmena) payload.bypassGate = zmena.bezBrany;
  if ('manazerId' in zmena) payload.managerId = zmena.manazerId;
  if ('aktivni' in zmena) payload.isActive = zmena.aktivni;
  if ('topmanagement' in zmena) payload.isTopManagement = zmena.topmanagement;
  return api.saveStatisticsSalesperson(id, payload);
}

/** Manažer: role, sazba superprovize, rozsah týmu, omezení typu média. */
export function ulozManazera(id, zmena) {
  const payload = {};
  if ('role' in zmena) payload.role = zmena.role;
  if ('superprovize' in zmena) payload.superCommissionPct = zmena.superprovize;
  if ('rozsah' in zmena) payload.superScope = zmena.rozsah;
  if ('typMedia' in zmena) payload.superMediaType = zmena.typMedia;
  if ('aktivni' in zmena) payload.isActive = zmena.aktivni;
  return api.saveStatisticsManager(id, payload);
}

/** Mentoring — založení i úprava; `id` null zakládá nový. */
export function ulozMentoring(id, dvojice) {
  return api.saveStatisticsMentorship(id, {
    menteeId: dvojice.mentorovanyId,
    mentorId: dvojice.mentorId,
    pct: dvojice.pct,
    validFrom: dvojice.od,
    validTo: dvojice.do || null,
    note: dvojice.poznamka || null,
  });
}

export function smazMentoring(id) {
  return api.deleteStatisticsMentorship(id);
}

/** Udělá z obchodníka manažera. */
export function pridejManazera(obchodnikId, zmena = {}) {
  return api.createStatisticsManager({
    salespersonId: obchodnikId,
    role: zmena.role ?? 'manažer regionu',
    superCommissionPct: zmena.superprovize ?? 0.01,
    superScope: zmena.rozsah ?? 'team',
    superMediaType: zmena.typMedia ?? null,
  });
}

/** Zařazení média pod provizní titul + velikost mutace a rozsah stran. */
export function ulozMedium(mediumId, { titulId, velikost, stran } = {}) {
  return api.assignStatisticsMedium(mediumId, {
    titleId: titulId ?? null,
    sizeCode: velikost ?? null,
    pages: stran ?? null,
  });
}

/* --------------------------------------------------- plány jednotlivých vydání */

/**
 * Vydání titulu i s ruční opravou plánu.
 *
 * `okno` je { od, do } ve tvaru `YYYY-MM-DD`; bez něj se vrátí celá historie
 * titulu (server ji stropuje na 500 řádků — „Redakční zpracování" jich má
 * přes čtyři tisíce).
 */
export async function nactiVydaniTitulu(titulId, okno = {}) {
  const { data } = await api.getStatisticsTitleIssues(titulId, {
    from: okno.od ?? null,
    to: okno.do ?? null,
  });

  return {
    titul: {
      id: data.title.id,
      nazev: data.title.name,
      plan: data.title.plan,
      // Měsíčně agregovaný titul počítá pásmo za celý měsíc, takže oprava
      // jednoho vydání by se nikde neprojevila — obrazovka ho jen vypíše.
      agregovany: data.title.monthlyAggregation || data.title.planFromMutations,
      mesicniAgregace: data.title.monthlyAggregation,
      planZMutaci: data.title.planFromMutations,
    },
    muzeUpravovat: data.canEdit !== false,
    celkem: data.total,
    vydani: data.issues.map((v) => ({
      klic: v.issueKey,
      projektId: v.projectId,
      nazev: v.name,
      datum: v.date,
      medium: v.mediumName,
      objem: v.volumeNet,
      // null = dědí se plán titulu
      prepis: v.override
        ? { p11: v.override.p11, p14: v.override.p14, p17: v.override.p17, p20: v.override.p20,
            duvod: v.override.reason, kdy: v.override.updatedAt }
        : null,
    })),
  };
}

/**
 * Uloží opravu plánu jednoho vydání. Posílá se jen to, co se změnilo —
 * neposlaná hranice zůstane zděděná z titulu, takže se pozdější změna plánu
 * titulu do vydání propíše.
 */
export function ulozPlanVydani(klic, zmena, duvod = null) {
  return api.saveCommissionPlanOverride({ issueKey: klic, reason: duvod, ...zmena });
}

/** Vrátí vydání na plán titulu (smaže opravu). */
export function zrusPlanVydani(klic) {
  return api.saveCommissionPlanOverride({ issueKey: klic, p11: null, p14: null, p17: null, p20: null });
}
